import { HttpErrorResponseExt } from '@aex/ngx-toolbox';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

@Injectable({ providedIn: 'root' })
export class HttpErrorService {

	constructor(
		private readonly toast: ToastrService,
	) { }

	private static getBadRequestErrors(error: IServerError | string): string {
		const lError: IServerError = typeof error === 'string' ? JSON.parse(error) : error;
		const errorList = lError.full_errors;
		return errorList && Array.isArray(errorList) ? errorList[0] : lError.error;
	}

	private static getErrorMsg(error: HttpErrorResponse, namespace: string): string {

		const messages: { [category: string]: { [code: number]: string | StringFunction } } = {
			authentication: {
				[HttpStatusCode.BadRequest]: 'Could not complete the request. Please try again.',
				[HttpStatusCode.Unauthorized]: 'User unauthorized. Contact Administrator',
				[HttpStatusCode.GatewayTimeout]: 'Server timeout. Please try again.',
			},
			area: {
				[HttpStatusCode.NotFound]: 'Area not found.',
			},
			workOrderAsset: {
				[HttpStatusCode.BadRequest]: 'Assets could not be saved. Please try again.',
				[HttpStatusCode.NotFound]: null,
			},
			serviceAsset: {
				[HttpStatusCode.BadRequest]: 'Assets could not be saved. Please try again.',
				[HttpStatusCode.NotFound]: null,
			},
			deviceManagement: {
				[HttpStatusCode.BadRequest]: 'Device could not be updated. Please try again.',
				[HttpStatusCode.NotFound]: 'Service not found',
			},
			kml: {
				[HttpStatusCode.BadRequest]: 'KML file is invalid',
				[HttpStatusCode.NotFound]: 'KML not found',
				[HttpStatusCode.InternalServerError]: 'KML file is invalid',
			},
			premise: {
				[HttpStatusCode.BadRequest]: 'Premise could not be updated. Please try again.',
				[HttpStatusCode.NotFound]: 'Premise or Service not found',
			},
			service: {
				[HttpStatusCode.BadRequest]: 'Could not complete the request. Please try again.',
				[HttpStatusCode.NotFound]: 'Service not found',
			},
			speedTest: {
				[HttpStatusCode.BadRequest]: 'Could not create the Speed Test. Please try again after 24 Hours.',
				[HttpStatusCode.GatewayTimeout]: 'Server timeout. Please try again.',
				[HttpStatusCode.NotFound]: null,
			},
			solarStatus: {
				[HttpStatusCode.BadRequest]: 'Plant Data could not be refreshed',
				[HttpStatusCode.NotFound]: 'Plant Not Found',
			},
			workOrder: {
				[HttpStatusCode.BadRequest]: 'Work order could not be created. Please try again.',
				[HttpStatusCode.NotFound]: 'Work order not found',
			},
			serviceStatus: {
				[HttpStatusCode.BadRequest]: 'Device Could Not Be Refreshed. Please try again.',
				[HttpStatusCode.NotFound]: 'Error - Please try again.',
			},
			wifiStatus: {
				[HttpStatusCode.BadRequest]: 'Wi-Fi Could Not Be Refreshed. Please try again.',
				[HttpStatusCode.NotFound]: null,
			},
			wifiInformation: {
				[HttpStatusCode.BadRequest]: 'Wi-Fi Could Not Be Refreshed. Please try again.',
				[HttpStatusCode.NotFound]: 'Device not found',
			},
			wifiDevices: {
				[HttpStatusCode.BadRequest]: 'Unable To Refresh Connected Devices. Please try again.',
				[HttpStatusCode.NotFound]: 'Device not found',
			},
			paymentHistory: {
				[HttpStatusCode.NotFound]: null,
			},
			default: {
				[HttpStatusCode.NotFound]: 'Resource not found',
				[HttpStatusCode.InternalServerError]: 'Server Error. Please try again',
				[HttpStatusCode.GatewayTimeout]: 'Server timeout. Please try again',
				[HttpStatusCode.Unauthorized]: 'User unauthorized. Contact Administrator',
			},
		};

		let message = messages[namespace] && messages[namespace][error.status];
		if (error.status === HttpStatusCode.BadRequest) {
			const serverError = HttpErrorService.getBadRequestErrors(error.error);
			message = typeof message === 'function' ? message(serverError) : serverError || message;
		}
		message = message === undefined ? messages[DEFAULT_NAMESPACE][error.status] : message;
		return typeof message === 'function' ? message() : message;
	}

	public handleError(error: HttpErrorResponse, namespace: string | boolean): HttpErrorResponse {
		const lNamespace = typeof namespace === 'string' ? namespace : DEFAULT_NAMESPACE;
		const message = HttpErrorService.getErrorMsg(error, lNamespace);
		if (message !== undefined) {
			if (message)
				this.toast.error(message);
			return new HttpErrorResponseExt(error, message, true);
		} else
			return error;
	}

	public handleWarning(error: HttpErrorResponse, namespace: string | boolean): HttpErrorResponse {
		const lNamespace = typeof namespace === 'string' ? namespace : DEFAULT_NAMESPACE;
		const message = HttpErrorService.getErrorMsg(error, lNamespace);
		if (message !== undefined) {
			if (message)
				this.toast.warning(message);
			return new HttpErrorResponseExt(error, message, true);
		} else
			return error;
	}

}

type StringFunction = (message?: string) => string;

const DEFAULT_NAMESPACE = 'default';

interface IServerError {
	error: string
	full_errors: string[]
}
